import { forwardRef } from 'react';
import type { PropsWithoutRef, Ref, CSSProperties, SyntheticEvent } from 'react';
import styled, { css } from 'styled-components';
import type { DefaultTheme } from 'styled-components';

import Icon from '../Icon';
import type { ForwardRefForwardPropsComponent, BaseProps, OmitStrict } from '../../types';
import { defaultThemeProp } from '../../theme';
import Flex from '../Flex';
import Image, { StyledImage } from '../Image';
import { useTheme } from '../../hooks';

import type { AttachmentType } from './utils';

export interface FileVisualProps extends BaseProps {
  /** Attachment/File type, it has an impact on background color and icon rendered. */
  type: AttachmentType;
  /** A string to be used as an image src for a attachment thumbnail. */
  thumbnail?: string;
  /** Called when a thumbnail encounters an error while loading. */
  onThumbnailError?: (e: SyntheticEvent<HTMLImageElement>) => void;
  /** Reference to a button element. */
  ref?: Ref<HTMLDivElement>;
}
type ThemeColor = keyof OmitStrict<DefaultTheme['base']['colors'], 'white' | 'black'>;
type ThemeColorVariant = 'extra-light' | 'light' | 'medium' | 'dark' | 'extra-dark';

type BackgroundColor = `${ThemeColor}.${ThemeColorVariant}`;

const mapping: Record<
  AttachmentType,
  { backgroundColor: BackgroundColor } & (
    | { iconName: string; text?: never }
    | { iconName?: never; text: string }
  )
> = {
  generic: {
    iconName: 'paper-clip',
    backgroundColor: 'slate.dark'
  },
  video: {
    iconName: 'play-solid',
    backgroundColor: 'slate.dark'
  },
  audio: {
    iconName: 'speaker-on-solid',
    backgroundColor: 'slate.dark'
  },
  document: {
    iconName: 'filetype-text',
    backgroundColor: 'blue.medium'
  },
  message: {
    iconName: 'mail-solid',
    backgroundColor: 'blue.dark'
  },
  pdf: {
    text: 'PDF',
    backgroundColor: 'red.medium'
  },
  spreadsheet: {
    iconName: 'grid-solid',
    backgroundColor: 'green.medium'
  },
  presentation: {
    iconName: 'slideshow-solid',
    backgroundColor: 'orange.medium'
  },
  link: {
    iconName: 'chain',
    backgroundColor: 'purple.dark'
  },
  archive: {
    iconName: 'archive-solid',
    backgroundColor: 'slate.dark'
  },
  database: {
    iconName: 'disc-stack-solid',
    backgroundColor: 'slate.dark'
  },
  image: {
    iconName: 'picture-solid',
    backgroundColor: 'slate.dark'
  },
  text: {
    iconName: 'filetype-text',
    backgroundColor: 'slate.dark'
  }
} as const;

export const StyledFileVisual = styled.div``;

export const StyledTextFrame = styled(StyledFileVisual)(({ theme }) => {
  const {
    base: {
      palette: { light },
      'border-radius': baseBorderRadius,
      'font-size': baseFontSize,
      'font-weight': { 'semi-bold': semiBoldWeight }
    },
    components: {
      icon: {
        size: { m: iconSize },
        'border-radius-multiplier': borderRadiusMultiplier
      }
    }
  } = theme;
  return css`
    background-color: var(--bg);
    color: ${light};
    height: ${iconSize};
    width: ${iconSize};
    border-radius: calc(${borderRadiusMultiplier} * ${baseBorderRadius});
    font-size: calc(0.7 * ${baseFontSize});
    font-weight: ${semiBoldWeight};
    user-select: none;
    -webkit-user-select: none;
  `;
});

StyledTextFrame.defaultProps = defaultThemeProp;

const StyledThumbnailFrame = styled(StyledFileVisual)(({ theme }) => {
  return css`
    background-color: ${theme.base.palette['app-background']};
    height: 100%;
    width: 100%;
    overflow: hidden;

    ${StyledImage} {
      margin: auto;
      height: unset;
      width: unset;
      max-height: 75%;
      max-width: 75%;
    }
  `;
});

StyledThumbnailFrame.defaultProps = defaultThemeProp;

const FileVisual: ForwardRefForwardPropsComponent<FileVisualProps> = forwardRef(function FileVisual(
  { type, thumbnail, onThumbnailError, ...restProps }: PropsWithoutRef<FileVisualProps>,
  ref: FileVisualProps['ref']
) {
  const visualConfig = mapping[type];

  const [color, lightness] = visualConfig.backgroundColor.split('.', 2) as [
    ThemeColor,
    ThemeColorVariant
  ];

  const {
    base: {
      colors: {
        [color]: { [lightness]: backgroundColor }
      },
      palette: { light: foregroundColor }
    }
  } = useTheme();

  if (thumbnail) {
    return (
      <Flex {...restProps} as={StyledThumbnailFrame} container>
        <Image src={thumbnail} onError={onThumbnailError} alt='' />
      </Flex>
    );
  }

  return (
    <>
      {visualConfig.iconName ? (
        <Icon
          {...restProps}
          ref={ref}
          name={visualConfig.iconName}
          background={backgroundColor}
          foreground={foregroundColor}
        />
      ) : (
        <Flex
          {...restProps}
          as={StyledTextFrame}
          ref={ref}
          container={{ justify: 'center', alignItems: 'center' }}
          style={{ '--bg': backgroundColor } as CSSProperties}
        >
          {visualConfig.text}
        </Flex>
      )}
    </>
  );
});

export default FileVisual;
